
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, ArrowRight, X, SquarePen, Play, ChartSpline, ImageDown, Image, ImagePlay, Text, FileText, FlaskConical, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, ListPlus, Globe, Database } from 'lucide-react';
import { visualQaExperimentalApiHost, visualQaDataUploadApiHost } from './config/apiConfig';
import VisualQaHandler from "./helpers/visualQaHandler";
import { getStage, getRegion } from './helpers/appUtils.js';
import { initializeAmplify } from './auth/intitalizeCognito'
import { Auth, Hub } from "aws-amplify";
import AnnotationUploader from './helpers/annotationUploader';
import ImageUploader from './helpers/imageUploader';

const IMAGE_FALLBACK = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="400" viewBox="0 0 600 400"%3E%3Crect width="600" height="400" fill="%23e5e7eb"/%3E%3Ctext x="300" y="205" text-anchor="middle" font-family="sans-serif" font-size="24" fill="%234b5563"%3EImage unavailable%3C/text%3E%3C/svg%3E';

// VisualQA Logo Component
const VisualQALogo = () => (
  <div className="flex items-center mr-4 self-center">
    <span className="font-medium text-2xl bg-gradient-to-r from-sky-700 to-sky-600 text-transparent bg-clip-text">VisualQA</span>
  </div>
);

// Enlarge Image Modal Component
const EnlargeImageModal = ({ show, onClose, imageUrl }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-graphite-gray bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="relative max-w-3xl w-full max-h-[90vh] overflow-auto bg-bone-white rounded-lg">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 p-1.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md active:scale-[0.99]"
        >
          <X size={20} />
        </button>
        <img
          src={imageUrl}
          alt="Enlarged Preview"
          className="w-full h-auto rounded-lg"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = IMAGE_FALLBACK;
          }}
        />
      </div>
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ show, onClose, imageUrlInput, setImageUrlInput, displayedImageUrl, setDisplayedImageUrl, openEnlargeModal, imageUploader, setUploadedImageUrl }) => {
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);
  
  const handleImageUrlChange = (e) => {
    const url = e.target.value;
    setImageUrlInput(url);
    if (url.trim() !== '') {
      setDisplayedImageUrl(url);
    } else {
      setDisplayedImageUrl('');
    }
  };
  
  const handleFileSelect = async (file) => {
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setDisplayedImageUrl(e.target.result);
      };
      reader.readAsDataURL(file);
      
      // Upload to S3
      setIsUploading(true);
      try {
        const s3Url = await imageUploader.uploadImageToS3(file);
        setImageUrlInput(s3Url);
      } catch (error) {
        console.error('Upload failed:', error);
        // Clear the displayed image and show error
        setDisplayedImageUrl('');
        setImageUrlInput('');
        alert('Upload failed, try again with another image or use an image from the web');
      } finally {
        setIsUploading(false);
      }
    }
  };
  
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFileSelect(file);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };
  
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      if (items[i].type.startsWith('image/')) {
        const file = items[i].getAsFile();
        handleFileSelect(file);
        break;
      }
    }
  };
  
  useEffect(() => {
    if (show) {
      document.addEventListener('paste', handlePaste);
      return () => document.removeEventListener('paste', handlePaste);
    }
  }, [show]);
  
  const handleDoneClick = () => {
    setUploadedImageUrl(imageUrlInput || displayedImageUrl);
    onClose();
  };
  
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-8 w-full max-w-md relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-sky-600 text-white p-1.5 rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md active:scale-[0.99]"
        >
          <X size={20} />
        </button>
        <h2 className="text-2xl font-medium text-graphite-gray mb-6">Upload Image</h2>

        {/* File Upload Area */}
        <div 
          className={`mb-6 border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragOver ? 'border-sky-500 bg-sky-50' : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload className="mx-auto mb-3 text-gray-400" size={32} />
          <p className="text-sm text-gray-600 mb-2">Drag and drop an image, paste from clipboard, or</p>
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="px-4 py-2 bg-sky-600 text-white text-sm rounded-lg hover:bg-sky-700 transition-colors"
          >
            Choose File
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>

        {/* URL Input */}
        <div className="mb-6">
          <p className="text-sm text-gray-600 mb-2 text-center">Or enter image URL:</p>
          <input
            type="text"
            id="imageUrl"
            className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-500 transition-all duration-200 ease-in-out"
            placeholder={isInputFocused || imageUrlInput ? '' : 'Enter image URL here'}
            value={imageUrlInput}
            onChange={handleImageUrlChange}
            onFocus={() => setIsInputFocused(true)}
            onBlur={() => setIsInputFocused(false)}
          />
        </div>

        {displayedImageUrl && (
          <div className="mb-6 text-center">
            <p className="text-sm font-medium text-graphite-gray mb-2">
              {isUploading ? 'Uploading to S3...' : 'Preview:'}
            </p>
            <div className="relative">
              <img
                src={displayedImageUrl}
                alt="Preview"
                className={`w-full h-40 object-cover rounded-lg border border-gray-300 cursor-pointer ${isUploading ? 'opacity-50' : ''}`}
                onClick={() => !isUploading && openEnlargeModal(displayedImageUrl)}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = IMAGE_FALLBACK;
                }}
              />
              {isUploading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 border-4 border-t-4 border-t-sky-600 border-gray-300 rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6">
          <button
            onClick={handleDoneClick}
            className="px-4 py-2 bg-sky-600 text-white font-medium rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md active:scale-[0.99]"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// LoadingSpinner.jsx
const LoadingSpinner = ({ loadingText = "Loading..." }) => (
  <div className="flex flex-col items-center justify-center flex-grow w-full">
    <div className="w-16 h-16 border-4 border-t-4 border-t-sky-800 border-sky-700 rounded-full animate-spin mb-4"></div>
    <p className="text-lg font-medium text-sky-800">{loadingText}</p>
  </div>
);

const SearchBar = ({ searchTerm, setSearchTerm, openUploadModal, uploadedImageUrl, handleSearchSubmit, imageSelected, setImageSelected, textSelected, setTextSelected }) => {
  return (
      <div className="flex-grow rounded-xl bg-gradient-to-r from-gray-200 to-gray-100 shadow-sm border-1 drop-shadow-sm border-white backdrop-blur-xl flex items-center p-0 mt-6">
        <input
          type="text"
          placeholder="Ask anything..."
          className="flex-grow text-graphite-gray placeholder-gray-500 focus:outline-none px-4 py-3 text-md"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        {uploadedImageUrl && (
          <img
            src={uploadedImageUrl}
            alt="Preview"
            className="w-8 h-8 object-cover rounded-full ml-2 mr-2"
            onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
          />
        )}
        <div className="flex items-center space-x-2 pr-2">
          <button className="p-2 text-gray-800 hover:text-sky hover:text-sky-800 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99]" onClick={openUploadModal}>
            <Upload size={20} />
          </button>
          
          <div className="flex bg-white rounded-xl p-1">
            <button 
              className={`p-1 mr-1 rounded-md transition-all duration-200 ease-in-out relative group ${imageSelected ? 'border-1 border-sky-800 bg-sky-100 text-sky-800 shadow-md' : 'text-gray-600'}`}
              onClick={() => setImageSelected(!imageSelected)}
            >
              <ImageDown size={18} />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
                ground with images
              </div>
            </button>
            <button 
              className={`p-1 mr-1 rounded-md transition-all duration-200 ease-in-out relative group ${textSelected ? 'border-1 border-sky-800 bg-sky-100 text-sky-800 shadow-md' : 'text-gray-600'}`}
              onClick={() => setTextSelected(!textSelected)}
            >
              <FileText size={18} />
              <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-200">
                ground with text from web and graph
              </div>
            </button>

          </div>
          
          <button onClick={() => handleSearchSubmit(true)} className="p-2 text-gray-800 hover:text-sky-800 cursor-pointer rounded-full transition-all duration-200 ease-in-out active:scale-[0.99]">
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
  );
};

// Initial Search Screen (adapted to match header search bar)
const InitialSearchScreen = ({ searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, imageSelected, setImageSelected, textSelected, setTextSelected }) => (
  <div className="flex flex-col items-center justify-center min-h-screen bg-bone-white text-graphite-gray p-4">
    <VisualQALogo />
    <div className="w-full max-w-2xl flex items-center p-2 mb-4">
      <SearchBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleSearchSubmit={handleSearchSubmit}
        openUploadModal={openUploadModal}
        uploadedImageUrl={uploadedImageUrl}
        imageSelected={imageSelected}
        setImageSelected={setImageSelected}
        textSelected={textSelected}
        setTextSelected={setTextSelected}
      />
    </div>
    {errorMessage && (
      <p className="text-red-500 text-sm mt-2 leading-relaxed">
        {errorMessage}
      </p>
    )}
    <div className="flex justify-center space-x-6 mt-16 text-graphite-gray text-sm">
      <p>Made by MMIR</p>
    </div>
  </div>
);

  // Main Interaction Screen (combining header and dynamic content)
  const MainInteractionScreen = ({ searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, setShowResults, qaResponses, imageSelected, setImageSelected, textSelected, setTextSelected }) => (
    <div className="flex flex-col flex-grow bg-bone-white text-graphite-gray min-h-screen"> 
      <Header
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        errorMessage={errorMessage}
        handleSearchSubmit={handleSearchSubmit}
        openUploadModal={openUploadModal}
        uploadedImageUrl={uploadedImageUrl}
        setShowResults={setShowResults}
        imageSelected={imageSelected}
        setImageSelected={setImageSelected}
        textSelected={textSelected}
        setTextSelected={setTextSelected}
      />
      <div className="w-full max-w-6xl mx-auto py-10 px-4"> 
        <MainResultsContent qaResponses={qaResponses} />
      </div>
      {/* Modals are now rendered at the App level */}
    </div>
  );

// Common Header component for interaction screens
const Header = ({ searchTerm, setSearchTerm, errorMessage, handleSearchSubmit, openUploadModal, uploadedImageUrl, setShowResults, imageSelected, setImageSelected, textSelected, setTextSelected }) => (
  <div className="flex flex-col items-center w-full bg-bone-white text-graphite-gray p-4">
    <VisualQALogo />    
    {/* Top search bar for new queries - consolidated into one set */}
    <div className="w-full max-w-6xl flex items-center mb-4">
      <SearchBar 
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleSearchSubmit={handleSearchSubmit}
        openUploadModal={openUploadModal}
        uploadedImageUrl={uploadedImageUrl}
        imageSelected={imageSelected}
        setImageSelected={setImageSelected}
        textSelected={textSelected}
        setTextSelected={setTextSelected}
      />
    </div>
    {errorMessage && (
      <p className="text-red-500 text-sm mt-2 leading-relaxed">
        {errorMessage}
      </p>
    )}
  </div>
);

// MediaViewerModal component for full-screen image/video viewing
const MediaViewerModal = ({ isOpen, media, initialIndex, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  if (!isOpen || !media || media.length === 0) return null;

  const currentMedia = media[currentIndex];

  const goToNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % media.length);
  };

  const goToPrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + media.length) % media.length);
  };

  const handleThumbnailClick = (index) => {
    setCurrentIndex(index);
  };

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center z-[100] p-4 bg-black bg-opacity-70">
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
        aria-label="Close media viewer"
      >
        <X size={20} />
      </button>

      {/* Main Media Content */}
      <div className="flex flex-col items-center justify-center flex-grow w-full max-w-4xl">
        <div className="relative flex flex-col items-center justify-center w-full">
          <div className="flex items-center justify-center w-full h-[calc(100vh-200px)]">
            {currentMedia.type === 'image' ? (
              <img
                src={currentMedia.src}
                alt={currentMedia.caption}
                className="max-w-full max-h-full object-contain rounded-lg shadow-lg"
                onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
              />
            ) : (
              <div className="relative w-full h-full">
                <iframe 
                  src={currentMedia.source_url} 
                  className="w-full h-full rounded-lg shadow-lg"
                  title={currentMedia.caption || "Video player"}
                  frameBorder="0"
                  referrerPolicy="origin"
                  sandbox="allow-scripts allow-same-origin allow-forms"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  onError={(e) => { 
                    e.target.onerror = null;
                    const fallbackImage = document.createElement('img');
                    fallbackImage.src = IMAGE_FALLBACK;
                    fallbackImage.alt = 'Video unavailable';
                    fallbackImage.className = 'w-full h-full object-contain rounded-lg shadow-lg';
                    e.target.parentNode.replaceChild(fallbackImage, e.target);
                  }}
                  style={{ backgroundColor: '#000' }}
                />
              </div>
            )}
          </div>
          <p className="mt-2 text-white text-base text-center max-w-full overflow-hidden">
            {currentMedia.caption}
          </p>
        </div>
      </div>

      {/* Navigation Arrows for Main Media */}
      {media.length > 1 && (
        <>
          <button
            onClick={goToPrev}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
            aria-label="Previous media"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={goToNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-all duration-200 shadow-md"
            aria-label="Next media"
          >
            <ChevronRight size={20} />
          </button>
        </>
      )}

      {/* Carousel of all images/videos at the bottom */}
      <div className="w-full max-w-4xl mt-4 overflow-x-auto scrollbar-hide flex justify-center">
        <div className="flex flex-nowrap space-x-2">
          {media.map((item, index) => (
            <div
              key={index}
              className={`flex-none w-32 h-20 relative rounded-md overflow-hidden cursor-pointer
                ${index === currentIndex ? 'border-2 border-sky-500 shadow-lg' : 'border border-gray-300'}
                hover:opacity-80 transition-opacity`}
              onClick={() => handleThumbnailClick(index)}
            >
              <img
                src={item.type === 'image' ? item.src : (item.source_metadata?.thumbnail_url || item.src)}
                alt={`Thumbnail ${index + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
              />
              {item.type === 'video' && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30">
                  <Play size={16} className="text-white opacity-75" />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Main Results Content with Grounding Console
const MainResultsContent = ({ qaResponses }) => {
  const [isConsoleOpen, setIsConsoleOpen] = useState(false);
  const [isConsoleCollapsed, setIsConsoleCollapsed] = useState(false);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentImageViewerIndex, setCurrentImageViewerIndex] = useState(0);
  const [isVideoViewerOpen, setIsVideoViewerOpen] = useState(false);
  const [currentVideoViewerIndex, setCurrentVideoViewerIndex] = useState(0);
  const [isGroundingImageViewerOpen, setIsGroundingImageViewerOpen] = useState(false);
  const [currentGroundingImageIndex, setCurrentGroundingImageIndex] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [displayedAnswer, setDisplayedAnswer] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedSidebarImage, setSelectedSidebarImage] = useState(null);
  const [isEntitySidebarOpen, setIsEntitySidebarOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [sidebarView, setSidebarView] = useState('image'); // 'image' or 'entity'
  
  // Show tooltip and animation after typing is complete with 1 second delay
  useEffect(() => {
    if (!isTyping) {
      setShowTooltip(true)
      setShowAnimation(true)
      const hideTimer = setTimeout(() => {
          setShowTooltip(false);
          setShowAnimation(false);
        }, 5000);      
      return () => clearTimeout(hideTimer);
    }
  }, [isTyping]);
  
  // Typing animation for answer
  useEffect(() => {
    const answer = qaResponses?.mmlm_answer || 'No answer available';
    if (answer && isTyping) {
      let i = 0;
      const typingInterval = setInterval(() => {
        setDisplayedAnswer(answer.substring(0, i));
        i++;
        if (i > answer.length) {
          clearInterval(typingInterval);
          setIsTyping(false);
          setIsConsoleOpen(true); // Open console when typing finishes
        }
      }, 30);
      return () => clearInterval(typingInterval);
    }
  }, [qaResponses?.mmlm_answer, isTyping]);
  
  const handleGroundingConsoleClick = () => {
    setIsConsoleOpen(true);
    setIsConsoleCollapsed(false);
  };

  const handleCloseConsole = () => {
    setIsConsoleOpen(false);
    // Reset main content scale when console closes
    const mainContent = document.querySelector('main');
    if (mainContent) {
      mainContent.style.transform = 'scale(1)';
    }
  };

  const handleToggleCollapseConsole = () => {
    setIsConsoleCollapsed(prev => !prev);
    // Adjust main content scale based on console state
    const mainContent = document.querySelector('main');
    if (mainContent) {
      if (!isConsoleCollapsed) {
        // If collapsing, restore scale
        mainContent.style.transform = 'scale(0.98)';
      } else {
        // If expanding, reduce scale
        mainContent.style.transform = 'scale(0.95)';
      }
    }
  };

  const openImageViewer = (index) => {
    setCurrentImageViewerIndex(index);
    setIsImageViewerOpen(true);
  };

  const closeImageViewer = () => {
    setIsImageViewerOpen(false);
  };
  
  const openVideoViewer = (index) => {
    setCurrentVideoViewerIndex(index);
    setIsVideoViewerOpen(true);
  };

  const closeVideoViewer = () => {
    setIsVideoViewerOpen(false);
  };
  
  const openGroundingImageViewer = (index) => {
    setCurrentGroundingImageIndex(index);
    setIsGroundingImageViewerOpen(true);
  };

  const closeGroundingImageViewer = () => {
    setIsGroundingImageViewerOpen(false);
  };
  
  const openImageSidebar = (image) => {
    setSelectedSidebarImage(image);
    setSidebarView('image');
    setIsSidebarOpen(true);
    setIsEntitySidebarOpen(false);
  };

  const closeImageSidebar = () => {
    setIsSidebarOpen(false);
    setSelectedSidebarImage(null);
  };
  
  const openEntitySidebar = (entity) => {
    setSelectedEntity(entity);
    setSidebarView('entity');
    setIsEntitySidebarOpen(true);
    setIsSidebarOpen(false);
  };

  const closeEntitySidebar = () => {
    setIsEntitySidebarOpen(false);
    setSelectedEntity(null);
  };
  
  // No need for global function anymore as we're passing it directly as a prop

  // Process images from qaResponses for sidebar display
  const images = qaResponses?.mmir_images_aug?.map((item) => ({
    type: 'image',
    src: item.url,
    caption: item.source_metadata.caption || 'No caption available',
  })) || [];

  // Process videos from qaResponses for sidebar display
  const videos = qaResponses?.mmir_videos_aug?.map((item) => ({
    type: 'video',
    thumbnail_url: item.source_metadata?.thumbnail_url,
    source_url: item.source_url,
    caption: item.source_metadata?.title || 'No caption available',
  })) || [];

  // Process images for grounding console
  const groundingImages = qaResponses?.mmir_images_grounding?.map((item) => ({
    src: item.url,
    caption: item.source_metadata.caption || 'No caption available',
    url: item.url,
    source_metadata: item.source_metadata,
    entities: item.entities || [],
    ...item
  })) || [];

  return (
    <div className="w-full">
      {/* Main Content Area */}
      <main className="grid grid-cols-1 lg:grid-cols-10 gap-8">
        {/* Left Section - Text Content */}
        <section className="lg:col-span-7 space-y-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <Text className="mr-3" />
            <span className="text-blue font-medium text-xl">Answer</span>
          </h2>
          <div>
            <div className="text-lg leading-relaxed mb-4">
              <UnifiedMarkdownHighlighter 
                content={displayedAnswer}
                entityMap={isTyping ? {} : qaResponses?.answer_entity_map || {}}
                onEntityClick={openEntitySidebar}
                fullAnswer={qaResponses?.mmlm_answer || ''}
              />
            </div>
          </div>
          <button 
            className="flex items-center h-8 text-gray-500 text-sm relative group"
            onClick={handleGroundingConsoleClick}
            aria-label="Open grounding console"
          >
            <span className="absolute -left-4 inset-y-0 w-[calc(100%+2rem)] rounded-full opacity-0 bg-gray-100 group-hover:opacity-100 transition-opacity duration-300"></span>
            <FlaskConical strokeWidth={1.5} size={16} className="mr-1 z-10 relative text-gray-500 group-hover:text-sky-700 duration-300"/>
            <span className={`text-blue font-medium z-10 relative group-hover:text-sky-700 duration-300 ${showAnimation ? 'bg-gradient-to-r from-sky-900 to-sky-700 text-transparent bg-clip-text animate-pulse' : ''}`}>
              inspect grounding
            </span>
            {showTooltip && !isTyping && (
              <div className="absolute left-0 top-full mt-2 px-3 py-1 bg-gray-800 text-white text-xs rounded shadow-lg whitespace-nowrap z-20">
                click to see image and text grounding
              </div>
            )}
          </button>
        </section>

        <section className="lg:col-span-3 space-y-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <Image className="mr-1"/><span className="text-blue font-medium text-xl">Images (Augmentation)</span>
          </h2>
          <div className="grid grid-cols-2 gap-1 mb-8"> 
            {images.slice(0, 5).map((img, i) => ( 
              <div key={`main-img-${i}`} className="flex flex-col cursor-pointer" onClick={() => openImageViewer(i)}>
                <div className="rounded-md overflow-hidden shadow-lg aspect-video">
                  <img
                    src={img.src}
                    alt={img.caption}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                  />
                </div>
                <div className="mt-1">
                  <p className="text-gray-600 text-xs truncate">{truncateText(img.caption, 25)}</p>
                </div>
              </div>
            ))}
            {images.length > 5 && (
              <div className="relative rounded-md overflow-hidden shadow-md aspect-video group cursor-pointer" onClick={() => openImageViewer(0)}> {/* Opens first image in viewer */}
                <img
                  src={images[5]?.src || images[0]?.src || IMAGE_FALLBACK}
                  alt="View More Images"
                  className="w-full h-full object-cover rounded-md bg-black bg-opacity-40"
                  onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="flex items-center space-x-1 px-3 py-1 bg-white text-gray-800 text-sm rounded-full shadow-lg hover:bg-gray-300 hover:text-black transition-colors duration-300">
                    <span>View more</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <ImagePlay className="mr-1"/><span className="text-blue font-medium text-xl">Videos (Augmentation)</span>
          </h2>
          <div className="grid grid-cols-2 gap-1 mb-8">
            {videos.slice(0, 5).map((vid, i) => (
              <div key={`main-video-${i}`} className="flex flex-col cursor-pointer" onClick={() => openVideoViewer(i)}>
                <div className="relative rounded-md overflow-hidden shadow-lg aspect-video group">
                  <img
                    src={vid.thumbnail_url}
                    alt={vid.caption}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center transition-all duration-300">
                    <button className="flex items-center space-x-1 px-3 py-1 bg-white text-gray-800 text-sm rounded-full shadow-lg hover:bg-gray-300 hover:text-black transition-colors duration-300">
                      <Play className="mr-1" size={15}/> <span>Watch</span>
                    </button>
                  </div>
                </div>
                <div className="mt-1">
                  <p className="text-gray-600 text-xs truncate">{truncateText(vid.caption, 25)}</p>
                </div>
              </div>
            ))}
            {videos.length > 5 && (
              <div className="relative rounded-md overflow-hidden shadow-lg aspect-video group cursor-pointer" onClick={() => openVideoViewer(0)}>
                <img
                  src={videos[5]?.thumbnail_url || videos[0]?.thumbnail_url || IMAGE_FALLBACK}
                  alt="View More Videos"
                  className="w-full h-full object-cover rounded-xl"
                  onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <button className="flex items-center space-x-1 px-3 py-1 bg-white text-gray-800 text-sm rounded-full shadow-lg hover:bg-gray-300 hover:text-black transition-colors duration-300">
                    <span>Watch more</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          <h2 className="text-2xl font-bold mb-4 flex items-center">
            <ChartSpline className="mr-3"/><span className="text-blue font-medium text-xl">Glyph</span>
          </h2>
          <img
            src={IMAGE_FALLBACK}
            alt="Glyph representation"
            className="w-full h-auto rounded-xl"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = IMAGE_FALLBACK;
            }}
          />
        </section>
      </main>

      {/* Grounding Console */}
      <GroundingConsole
        isOpen={isConsoleOpen}
        onClose={handleCloseConsole}
        isCollapsed={isConsoleCollapsed}
        onToggleCollapse={handleToggleCollapseConsole}
        groundingImages={groundingImages}
        groundingText={qaResponses?.ngs_grounding || []}
        openGroundingImageViewer={openGroundingImageViewer}
        openImageSidebar={openImageSidebar}
        qaResponses={qaResponses}
      />
      
      {/* Unified Sidebar */}
      <UnifiedSidebar
        isOpen={isSidebarOpen || isEntitySidebarOpen}
        onClose={() => {
          closeImageSidebar();
          closeEntitySidebar();
        }}
        view={sidebarView}
        setView={setSidebarView}
        image={selectedSidebarImage}
        entity={selectedEntity}
        groundingImages={groundingImages}
        openImageSidebar={openImageSidebar}
      />
      
      {/* Image Viewer Modal */}
      <MediaViewerModal
        isOpen={isImageViewerOpen}
        media={images}
        initialIndex={currentImageViewerIndex}
        onClose={closeImageViewer}
      />

      {/* Video Viewer Modal */}
      <MediaViewerModal
        isOpen={isVideoViewerOpen}
        media={videos}
        initialIndex={currentVideoViewerIndex}
        onClose={closeVideoViewer}
      />
      
      {/* Grounding Image Viewer Modal */}
      <MediaViewerModal
        isOpen={isGroundingImageViewerOpen}
        media={groundingImages.map(img => ({ type: 'image', ...img }))}
        initialIndex={currentGroundingImageIndex}
        onClose={closeGroundingImageViewer}
      />
    </div>
  );
};

// Unified component that handles both markdown rendering and entity highlighting
const UnifiedMarkdownHighlighter = ({ content, entityMap, onEntityClick, fullAnswer }) => {


  const renderHighlightedContent = (text, entityMap, onEntityClick) => {
    if (!text) return null;
    
    // If no entity highlighting needed, just render markdown
    if (!entityMap || Object.keys(entityMap).length === 0) {
      return <div dangerouslySetInnerHTML={{ __html: parseMarkdown(text) }} />;
    }

    // Apply entity highlighting to raw text first
    let highlightedText = text;
    Object.entries(entityMap).forEach(([keyword, data], index) => {
      if (data.entities && keyword && keyword.trim() && text.toLowerCase().includes(keyword.toLowerCase()) && 
          data.entities.some(ent => ent.title || ent.description || ent.id)) {
        const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`\\b${escapedKeyword}\\b`, 'gi');
        highlightedText = highlightedText.replace(regex, (match) => {
          return `__ENTITY_${index}_START__${match}__ENTITY_${index}_END__`;
        });
      }
    });

    // Parse markdown with entity placeholders
    let processedHtml = parseMarkdown(highlightedText);
    
    // Replace placeholders with clickable spans
    Object.entries(entityMap).forEach(([keyword, data], index) => {
      if (data.entities && keyword && keyword.trim() && text.toLowerCase().includes(keyword.toLowerCase()) && 
          data.entities.some(ent => ent.title || ent.description || ent.id)) {
        const startPlaceholder = `__ENTITY_${index}_START__`;
        const endPlaceholder = `__ENTITY_${index}_END__`;
        const regex = new RegExp(`${startPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(.*?)${endPlaceholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'g');
        processedHtml = processedHtml.replace(regex, (match, entityText) => {
          return `<span class="bg-sky-100 hover:bg-sky-200 cursor-pointer px-1 rounded transition-colors duration-200" data-entity-keyword="${keyword.replace(/"/g, '&quot;')}">${entityText}</span>`;
        });
      }
    });

    return (
      <div 
        dangerouslySetInnerHTML={{ __html: processedHtml }}
        onClick={(e) => {
          const entityKeyword = e.target.getAttribute('data-entity-keyword');
          if (entityKeyword && entityMap[entityKeyword]) {
            onEntityClick({ keyword: entityKeyword, entities: entityMap[entityKeyword].entities });
          }
        }}
      />
    );
  };

  const parseMarkdown = (text) => {
    if (!text) return '';
    
    // First, split numbered lists that are inline
    text = text.replace(/(\d+\.)\s+/g, '\n$1 ');
    // Split dash bullet points that are inline
    text = text.replace(/([^\n])\s+-\s+/g, '$1\n- ');
    // Split star bullet points that are inline
    text = text.replace(/([^\n])\s+\*\s+/g, '$1\n* ');
    // Split headers that are inline
    text = text.replace(/([^\n])\s+(###\s+)/g, '$1\n$2');
    
    const lines = text.split('\n');
    const result = [];
    
    for (let i = 0; i < lines.length; i++) {
      let line = lines[i].trim();
      if (!line) continue;
      
      // Headers (including ### format)
      if (line.startsWith('### ')) {
        result.push(`<h3 class="text-lg font-semibold mt-4 mb-2">${line.slice(4)}</h3>`);
      } else if (line.startsWith('## ')) {
        result.push(`<h2 class="text-xl font-semibold mt-4 mb-2">${line.slice(3)}</h2>`);
      } else if (line.startsWith('# ')) {
        result.push(`<h1 class="text-2xl font-bold mt-4 mb-2">${line.slice(2)}</h1>`);
      }
      // Bullet points (both * and -)
      else if (line.match(/^[-*] /)) {
        const content = line.replace(/^[-*] /, '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        result.push(`<div class="flex items-start mb-1"><span class="mr-2">•</span><span>${content}</span></div>`);
      }
      // Numbered lists
      else if (line.match(/^\d+\. /)) {
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        result.push(`<div class="mb-2">${line}</div>`);
      }
      // Regular text
      else {
        // Apply inline formatting
        line = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*([^*]+)\*/g, '<em>$1</em>')
          .replace(/\[([^\]]+)\]\(([^\)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline" target="_blank">$1</a>');
        
        result.push(`<div class="mb-1">${line}</div>`);
      }
    }
    
    return result.join('');
  };

  return (
    <div className="markdown-content">
      {renderHighlightedContent(content, entityMap, onEntityClick)}
    </div>
  );
};

// UnifiedSidebar component for displaying both image and entity details
const UnifiedSidebar = ({ isOpen, onClose, view, setView, image, entity, groundingImages, openImageSidebar }) => {
  if (!isOpen) return null;

  const uniqueEntities = entity ? entity.entities.filter((ent, index, arr) => 
    arr.findIndex(e => e.id === ent.id) === index && 
    (ent.title || ent.description || ent.id)
  ) : [];
  const relatedImages = entity ? groundingImages.filter(img => {
    return img.entities && img.entities.some(imgEntity => 
      uniqueEntities.some(selectedEntity => 
        imgEntity.id === selectedEntity.id || 
        imgEntity.title === selectedEntity.title
      )
    );
  }) : [];

  return (
    <div className="fixed inset-y-0 right-0 w-96 bg-slate-900 border-l border-slate-700 shadow-xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
      <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800 flex-shrink-0">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => setView('image')}
            className={`px-3 py-1 text-sm rounded ${view === 'image' ? 'bg-sky-600 text-white' : 'text-gray-400 hover:text-white'}`}
            disabled={!image}
          >
            Image
          </button>
          <button
            onClick={() => setView('entity')}
            className={`px-3 py-1 text-sm rounded ${view === 'entity' ? 'bg-sky-600 text-white' : 'text-gray-400 hover:text-white'}`}
            disabled={!entity}
          >
            Entity
          </button>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-full hover:bg-slate-700 text-white transition-colors duration-200"
          aria-label="Close sidebar"
        >
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-hide">
        {view === 'image' && image && (
          <div className="p-4">
            <div className="mb-4">
              <img
                src={image.url}
                alt={image.source_metadata?.caption || 'Grounding image'}
                className="w-full h-48 object-cover rounded-lg"
                onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
              />
            </div>
            
            <div className="space-y-4 text-sm mb-6">
              {image.source_url && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source URL</div>
                  <div className="text-gray-300 break-all">{image.source_url}</div>
                </div>
              )}
              {image.source_metadata?.caption && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Caption</div>
                  <div className="text-gray-300">{image.source_metadata.caption}</div>
                </div>
              )}
              {image.source_metadata?.title && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Title</div>
                  <div className="text-gray-300">{image.source_metadata.title}</div>
                </div>
              )}
              {image.ims_source_title && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source Title</div>
                  <div className="text-gray-300">{image.ims_source_title}</div>
                </div>
              )}
              {image.ims_source_summary && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source Summary</div>
                  <div className="text-gray-300">{image.ims_source_summary}</div>
                </div>
              )}
              {image.ims_source_content && (
                <div>
                  <div className="text-sky-400 font-medium mb-1">Source Content</div>
                  <div className="text-gray-300">{image.ims_source_content}</div>
                </div>
              )}
            </div>
            
            {image.entities && image.entities.length > 0 && (
              <div>
                <h4 className="text-sky-400 font-medium mb-3">Entities ({image.entities.length})</h4>
                <div className="space-y-3">
                  {image.entities.map((ent, index) => (
                    <div key={index} className="bg-slate-800 p-3 rounded-lg">
                      <h5 className="text-white font-medium mb-2">{ent.title}</h5>
                      <p className="text-gray-300 text-sm mb-2">{ent.description}</p>
                      <div className="text-xs text-gray-400">
                        <span className="bg-slate-700 px-2 py-1 rounded">ID: {ent.id}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {view === 'entity' && entity && (
          <div className="p-4">
            <div className="mb-4">
              <h4 className="text-sky-400 font-medium mb-2">Keyword</h4>
              <p className="text-white text-lg">{entity.keyword}</p>
            </div>

            <div className="mb-6">
              <h4 className="text-sky-400 font-medium mb-3">Entities ({entity.entities.filter((ent, index, arr) => arr.findIndex(e => e.id === ent.id) === index).length})</h4>
              <div className="space-y-3">
                {entity.entities.filter((ent, index, arr) => arr.findIndex(e => e.id === ent.id) === index).map((ent, index) => (
                  <div key={ent.id} className="bg-slate-800 p-3 rounded-lg">
                    <h5 className="text-white font-medium mb-2">{ent.title}</h5>
                    <p className="text-gray-300 text-sm mb-2">{ent.description}</p>
                    <div className="text-xs text-gray-400">
                      <span className="bg-slate-700 px-2 py-1 rounded">ID: {ent.id}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {relatedImages.length > 0 && (
              <div>
                <h4 className="text-sky-400 font-medium mb-3">Related Grounding Images ({relatedImages.length})</h4>
                <div className="grid grid-cols-2 gap-2">
                  {relatedImages.map((img, index) => (
                    <div 
                      key={index}
                      className="cursor-pointer group"
                      onClick={() => openImageSidebar(img)}
                    >
                      <div className="rounded-lg overflow-hidden">
                        <img
                          src={img.src || img.url}
                          alt={img.caption || 'Related image'}
                          className="w-full h-24 object-cover group-hover:opacity-80 transition-opacity"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = IMAGE_FALLBACK;
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-400 mt-1 truncate">
                        {img.caption || 'No caption'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// GroundingConsole component
const GroundingConsole = ({ isOpen, onClose, isCollapsed, onToggleCollapse, groundingImages, groundingText, openGroundingImageViewer, openImageSidebar, qaResponses }) => {
  if (!isOpen) return null; // Don't render if not open

  return (
    <div
      className={`fixed inset-x-0 bottom-0 bg-slate-900 border-t border-slate-700 shadow-xl transition-all duration-300 ease-in-out z-50
        ${isCollapsed ? 'h-12' : 'h-2/5'}`}
      style={{ 
        height: isCollapsed ? '3rem' : '40vh',
        transform: 'translateY(0)',
      }}
      onAnimationEnd={() => {
        // Scale the main content when console appears
        const mainContent = document.querySelector('main');
        if (mainContent) {
          mainContent.style.transform = 'scale(0.95)';
          mainContent.style.transformOrigin = 'bottom';
          mainContent.style.transition = 'transform 0.3s ease-in-out';
        }
      }}
    >
      {/* Console Header */}
      <div className="flex items-center justify-between px-4 border-b border-slate-700 bg-slate-800">
        <h3 className="text-xs font-medium text-white">Grounding Console</h3>
        <div className="flex items-center space-x-4">
          {/* Collapse/Expand Button */}
          <button
            onClick={onToggleCollapse}
            className="p-2 rounded-full hover:bg-slate-700 text-white transition-colors duration-200"
            aria-label={isCollapsed ? "Expand console" : "Collapse console"}
          >
            {isCollapsed ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {/* Close Button */}
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-slate-700 text-white transition-colors duration-200"
            aria-label="Close console"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Console Content - Visible only when not collapsed */}
      {!isCollapsed && (
        <div className="flex flex-col h-full p-4 overflow-y-auto scrollbar-hide">
          {/* Image Grounding Section */}
          <div className="mb-4">
            <h4 className="text-xs mb-3 flex items-center text-sky-400">
              MMIR Image Observations
            </h4>
            <div className="flex flex-nowrap overflow-x-auto pb-4 -mb-4 scrollbar-hide space-x-1">
              {groundingImages && groundingImages.length > 0 ? (
                groundingImages.map((img, i) => (
                  <div 
                    key={`ground-img-${i}`} 
                    className="flex-none w-50 flex flex-col cursor-pointer"
                    onClick={() => openImageSidebar(qaResponses?.mmir_images_grounding?.[i])}
                  >
                    <div className="rounded-lg overflow-hidden shadow-sm h-24">
                      <img
                        src={img.src}
                        alt={img.caption}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.onerror = null; e.target.src = IMAGE_FALLBACK; }}
                      />
                    </div>
                    {/* Caption below the image */}
                    <div className="mt-1 text-xs text-gray-400">
                      <p className="truncate">{truncateText(img.caption || 'No caption available', 25)}</p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex-none w-full text-center py-4 text-gray-500 text-xs">
                  No grounding images available
                </div>
              )}
            </div>
          </div>

          {/* Text Grounding Section (Sources) */}
          <div>
            <h4 className="text-xs mb-3 text-sky-400">
              NGS Grounding
            </h4>
            
            <div className="flex flex-col space-y-4">
              {/* Combined Sources Section */}
              {groundingText && groundingText.length > 0 ? (
                <div className="flex flex-nowrap overflow-x-auto pb-4 scrollbar-hide space-x-2">
                  {groundingText.map((item, index) => (
                    <div 
                      key={`source-${index}`} 
                      className="flex-none w-80 p-3 rounded-lg shadow-sm flex flex-col justify-between bg-slate-800 border border-slate-700"
                    >
                      {/* Source (retrievalService) */}
                      <div className="bg-slate-700 text-sky-400 px-2 py-1 rounded-full text-xs mb-2 inline-block w-fit">
                        {item.retrievalService || "Source"}
                      </div>
                      
                      {/* Title */}
                      {item.title && (
                        <h5 className="font-semibold text-xs text-white mb-2">
                          {item.title}
                        </h5>
                      )}
                      
                      {/* Text content */}
                      <p className="text-xs text-gray-300 mb-2">
                        {item.text || "No text available"}
                      </p>
                      
                      {/* URL */}
                      {item.url && (
                        <div className="text-xs text-sky-400 truncate mt-auto">
                          {item.url}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex-none w-full text-center py-4 text-gray-500 text-xs">
                  No grounding text available
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to truncate text
const truncateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
};



// Annotate Modal Component  
const AnnotateModal = ({ show, onClose, data, setData, step, setStep, searchTerm, uploadedImageUrl, annotationUploader, qaResponses, visualQaHandler, imageSelected, textSelected }) => {
  const [annotationModelData, setAnnotationModelData] = useState(null);
  const [isLoadingAnnotationData, setIsLoadingAnnotationData] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const scrollContainerRef = useRef(null);
  const qaResponsesRef = useRef(qaResponses);
  
  // Reset annotation data when qaResponses actually change (new search)
  useEffect(() => {
    if (qaResponsesRef.current !== qaResponses) {
      setAnnotationModelData(null);
      qaResponsesRef.current = qaResponses;
    }
  }, [qaResponses]);
  
  useEffect(() => {
    if (show && !annotationModelData && visualQaHandler) {
      const loadAnnotationData = async () => {
        setIsLoadingAnnotationData(true);
        try {
          const grounding_images = qaResponses?.mmir_images_grounding || [];
          const modelData = await visualQaHandler.constructAnnotationModel(
            searchTerm,
            uploadedImageUrl,
            textSelected,
            imageSelected,
            true,
            qaResponses,
            grounding_images
          );
          setAnnotationModelData(modelData);
        } catch (error) {
          console.error('Error loading annotation data:', error);
          onClose('annotation_failed');
        } finally {
          setIsLoadingAnnotationData(false);
        }
      };
      loadAnnotationData();
    }
  }, [show, annotationModelData, visualQaHandler, searchTerm, uploadedImageUrl, textSelected, imageSelected, qaResponses, onClose]);
  
  console.log('AnnotateModal render:', { show, step, data });
  if (!show) return null;

  if (isLoadingAnnotationData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="w-12 h-12 border-4 border-t-4 border-t-sky-800 border-sky-700 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg font-medium text-sky-800">Preparing your annotation exercise...</p>
        </div>
      </div>
    );
  }

  const totalSteps = 5;
  const answers = [
    annotationModelData?.text_grounding_answer || "No text grounding answer available",
    annotationModelData?.image_grounding_answer || "No image grounding answer available",
    annotationModelData?.parametric_answer || "No parametric answer available"
  ];

  const options = [
    { value: 'incorrect', label: 'Incorrect' },
    { value: 'partial', label: 'Partially correct' },
    { value: 'correct', label: 'Correct' }
  ];

  const images = (annotationModelData?.images || []).slice(0, 10).map((img, i) => ({
    id: i,
    src: img.displayUrl,
    metadata: {
      sourceUrl: img?.sourceUrl || 'N/A',
      caption: img?.caption || 'N/A',
      title: img?.title || 'N/A',
      sourceTitle: img?.sourceTitle || 'N/A',
      sourceSummary: img?.sourceSummary || 'N/A',
      sourceContent: img?.sourceContent || 'N/A'
    }
  }));

  const relevancyOptions = [
    { value: 'not_relevant', label: 'Not relevant', color: 'text-red-800' },
    { value: 'somewhat_relevant', label: 'Somewhat relevant', color: 'text-yellow-800' },
    { value: 'highly_relevant', label: 'Highly relevant', color: 'text-green-800' }
  ];

  const getStatusColor = (value) => {
    switch(value) {
      case 'highly_relevant':
      case 'highly_useful':
        return 'text-green-800';
      case 'somewhat_relevant':
      case 'somewhat_useful':
        return 'text-yellow-800';
      case 'not_relevant':
        return 'text-red-800';
      default:
        return 'text-gray-700';
    }
  };

  const handleRadioChange = (answerIndex, value) => {
    setData(prev => ({
      ...prev,
      [`answer_${answerIndex}`]: value
    }));
  };

  const handleImageRelevancyChange = (imageIndex, value) => {
    setData(prev => ({
      ...prev,
      [`image_${imageIndex}`]: value
    }));
  };

  const handleMetadataRelevancyChange = (imageIndex, value) => {
    setData(prev => ({
      ...prev,
      [`metadata_${imageIndex}`]: value
    }));
  };

  const isStep1Complete = answers.every((_, index) => data[`answer_${index}`]);
  const isStep2Complete = images.every((_, index) => data[`image_${index}`]) && data.overall_grounding_utility_image;
  
  const relevantImagesForUtility = images.filter((_, index) => 
    data[`image_${index}`] === 'somewhat_relevant' || data[`image_${index}`] === 'highly_relevant'
  );
  const isStep3Complete = relevantImagesForUtility.every((image) => {
    const originalIndex = images.findIndex(img => img.id === image.id);
    return data[`image_utility_${originalIndex}`];
  });
  
  const isStep4Complete = images.every((_, index) => data[`metadata_${index}`]) && data.overall_metadata_utility_image;
  
  const relevantMetadataForUtility = images.filter((_, index) => 
    data[`metadata_${index}`] === 'somewhat_relevant' || data[`metadata_${index}`] === 'highly_relevant'
  );
  const isStep5Complete = relevantMetadataForUtility.every((image) => {
    const originalIndex = images.findIndex(img => img.id === image.id);
    return data[`metadata_utility_${originalIndex}`];
  });

  const handleNext = () => {
    if (step === 1) {
      const allNotRelevant = images.every((_, index) => data[`image_${index}`] === 'not_relevant');
      if (allNotRelevant) {
        setStep(3); 
        setTimeout(() => {
          if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
          }
        }, 0);
        return;
      }
    }
    if (step === 3) {
      const allMetadataNotRelevant = images.every((_, index) => data[`metadata_${index}`] === 'not_relevant');
      if (allMetadataNotRelevant) {
        return; 
      }
    }
    if (step < totalSteps - 1) {
      setStep(step + 1);
      setTimeout(() => {
        if (scrollContainerRef.current) {
          scrollContainerRef.current.scrollTop = 0;
        }
      }, 0);
    }
  };

  const getStepTitle = () => {
    if (step === 0) return "Step 1: Answer Correctness";
    if (step === 1) return "Step 2: Image Evaluation";
    if (step === 2) return "Step 3: Granular Image Utility";
    if (step === 3) return "Step 4: Metadata Evaluation";
    if (step === 4) return "Step 5: Granular Metadata Utility";
    return `Step ${step + 1}`;
  };

  const renderStepContent = () => {
    if (step === 0) {
      return (
        <>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700 mb-2">Label the given answer texts as follows:</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Incorrect</strong> – The answer is wrong, misleading, or does not address the question.</li>
              <li><strong>2. Partially correct</strong> – The answer includes some correct elements, but is incomplete or contains minor errors.</li>
              <li><strong>3. Correct</strong> – The answer matches the reference in meaning and covers all required information accurately.</li>
            </ol>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left p-4 font-medium text-gray-700">Answer</th>
                  <th className="text-center p-4 font-medium text-gray-700">Incorrect</th>
                  <th className="text-center p-4 font-medium text-gray-700">Partially correct</th>
                  <th className="text-center p-4 font-medium text-gray-700">Correct</th>
                </tr>
              </thead>
              <tbody>
                {answers.map((answer, index) => (
                  <tr key={index} className="border-b border-gray-100">
                    <td className="p-4 text-sm text-gray-800">{answer}</td>
                    {options.map((option) => (
                      <td key={option.value} className="p-4 text-center">
                        <input
                          type="radio"
                          name={`answer_${index}`}
                          value={option.value}
                          checked={data[`answer_${index}`] === option.value}
                          onChange={(e) => handleRadioChange(index, e.target.value)}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    } else if (step === 1) {
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Image Relevancy</h3>
            <p className="text-sm text-gray-600 mb-2">How relevant is the retrieved image to the input image, based on key entities mentioned in the user query.</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Not relevant</strong> – The retrieved image does not share any meaningful entities with the input image.</li>
              <li><strong>2. Somewhat relevant</strong> – The retrieved image includes entities that match the macro-category of those in the input image, but details differ (e.g., both images show a washing machine, but different models).</li>
              <li><strong>3. Highly relevant</strong> – The retrieved image includes exactly the same key entities as the input image, with matching granularity (e.g., same object and model).</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {images.map((image, index) => (
                <div key={image.id} className="flex items-center py-3">
                  <img
                    src={image.src}
                    alt={`Image ${index + 1}`}
                    className="w-20 h-15 object-cover rounded mr-4 flex-shrink-0"
                  />
                  <div className="flex space-x-6">
                    {relevancyOptions.map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          name={`image_${index}`}
                          value={option.value}
                          checked={data[`image_${index}`] === option.value}
                          onChange={(e) => handleImageRelevancyChange(index, e.target.value)}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${option.color}`}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <hr className="border-gray-300 mb-6" />
          
          <div className="mb-6 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Overall Grounding Utility of Retrieved Image</h3>
            <div className="mb-2 p-4 bg-sky-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2 font-medium">Following question is about the given answer:</p>
                <p className="text-sm text-gray-700 mb-2"><i>{answers[1]}</i></p>
            </div>
            <p className="text-sm text-gray-600 mb-2">Does any part of the retrieved image appear in the generated answer by the model?</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Yes</strong> – Some data from the retrieved multimodal response is used in the generated answer.</li>
              <li><strong>2. No</strong> – The retrieved data does not appear or contribute to the generated answer.</li>
            </ol>
          </div>
          
          <div className="flex space-x-6">
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_grounding_utility_image"
                value="yes"
                checked={data.overall_grounding_utility_image === 'yes'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_grounding_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_grounding_utility_image"
                value="no"
                checked={data.overall_grounding_utility_image === 'no'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_grounding_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
        </>
      );
    } else if (step === 2) {
      // Filter images to show only those marked as somewhat or highly relevant
      const relevantImages = images.filter((_, index) => 
        data[`image_${index}`] === 'somewhat_relevant' || data[`image_${index}`] === 'highly_relevant'
      );
      
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Granular Image Utility</h3>
            <p className="text-sm text-gray-600 mb-2">How useful is each retrieved image for answering the user query.</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Somewhat useful</strong> – The image includes helpful visual information, but not enough to fully answer the query.</li>
              <li><strong>2. Highly useful</strong> – The image alone provides all necessary visual information to answer the query.</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {relevantImages.map((image, displayIndex) => {
                const originalIndex = images.findIndex(img => img.id === image.id);
                return (
                  <div key={image.id} className="flex items-center py-3">
                    <img
                      src={image.src}
                      alt={`Image ${originalIndex + 1}`}
                      className="w-20 h-15 object-cover rounded mr-4 flex-shrink-0"
                    />
                    <div className="flex space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`image_utility_${originalIndex}`}
                          value="somewhat_useful"
                          checked={data[`image_utility_${originalIndex}`] === 'somewhat_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`image_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('somewhat_useful')}`}>Somewhat useful</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`image_utility_${originalIndex}`}
                          value="highly_useful"
                          checked={data[`image_utility_${originalIndex}`] === 'highly_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`image_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('highly_useful')}`}>Highly useful</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );
    } else if (step === 3) {
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Image Metadata Relevancy</h3>
            <p className="text-sm text-gray-600 mb-2">How relevant is the retrieved image meta-data to the user query, based on key entities.</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Not relevant</strong> – The retrieved image meta-data is unrelated to the user query.</li>
              <li><strong>2. Somewhat relevant</strong> – The image meta-data aligns with the macro-category of the entities in the query, but details differ or are incomplete.</li>
              <li><strong>3. Highly relevant</strong> – The image meta-data matches the key entities in the query at a fine-grained level.</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {images.map((image, index) => (
                <div key={image.id} className="py-3 border-b border-gray-100">
                  <div className="mb-3">
                    <div className="text-xs text-gray-500 space-y-1">
                      <div><strong>Source URL:</strong> {image.metadata.sourceUrl|| 'N/A'}</div>
                      <div><strong>Caption:</strong> {image.metadata.caption || 'N/A'}</div>
                      <div><strong>Title:</strong> {image.metadata.title || 'N/A'}</div>
                      <div><strong>Source Title:</strong> {image.metadata.sourceTitle || 'N/A'}</div>
                      <div><strong>Source Summary:</strong> {image.metadata.sourceSummary || 'N/A'}</div>
                      <div><strong>Source Content:</strong> {image.metadata.sourceContent || 'N/A'}</div>
                    </div>
                  </div>
                  <div className="flex space-x-6">
                    {relevancyOptions.map((option) => (
                      <label key={option.value} className="flex items-center">
                        <input
                          type="radio"
                          name={`metadata_${index}`}
                          value={option.value}
                          checked={data[`metadata_${index}`] === option.value}
                          onChange={(e) => handleMetadataRelevancyChange(index, e.target.value)}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${option.color}`}>{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          <hr className="border-gray-300 mb-6" />
          
          <div className="mb-6 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Overall Grounding Utility of Metadata</h3>
            <div className="mb-2 p-4 bg-sky-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-2 font-medium">Following question is about the given answer:</p>
                <p className="text-sm text-gray-700 mb-2"><i>{answers[1]}</i></p>
            </div>
            <p className="text-sm text-gray-600 mb-2">Does any part of the metadata appear in the generated answer by the model?</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Yes</strong> – Some data from the retrieved multimodal response is used in the generated answer.</li>
              <li><strong>2. No</strong> – The retrieved data does not appear or contribute to the generated answer.</li>
            </ol>
          </div>
          
          <div className="flex space-x-6">
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_metadata_utility_image"
                value="yes"
                checked={data.overall_metadata_utility_image === 'yes'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_metadata_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">Yes</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="overall_metadata_utility_image"
                value="no"
                checked={data.overall_metadata_utility_image === 'no'}
                onChange={(e) => {
                  const value = e.target.value;
                  setData(prev => ({ ...prev, overall_metadata_utility_image: value }));
                }}
                className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
              />
              <span className="text-sm text-gray-700">No</span>
            </label>
          </div>
        </>
      );
    } else if (step === 4) {
      // Filter images to show only those with metadata marked as somewhat or highly relevant
      const relevantMetadata = images.filter((_, index) => 
        data[`metadata_${index}`] === 'somewhat_relevant' || data[`metadata_${index}`] === 'highly_relevant'
      );
      
      return (
        <>
          <div className="mb-8 p-4 bg-sky-50 rounded-lg">
              <p className="text-sm text-gray-700 font-medium mb-2">Following question is about your query / image:</p>
              {uploadedImageUrl && (
                <div className="mt-3">
                  <p className="text-sm text-gray-700 mb-2">{searchTerm}</p>
                  <img
                    src={uploadedImageUrl}
                    alt="User provided image"
                    className="w-24 h-18 object-cover rounded"
                  />
                </div>
              )}
          </div>
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Granular Metadata Utility</h3>
            <p className="text-sm text-gray-600 mb-2">How useful is each retrieved metadata (text) for answering the user query</p>
            <ol className="text-sm text-gray-600 space-y-1">
              <li><strong>1. Somewhat useful</strong> – The text contains helpful information, but it's incomplete or insufficient for a full answer.</li>
              <li><strong>2. Highly useful</strong> – The text alone fully answers the query, with all required details.</li>
            </ol>
          </div>
          
          <div className="max-h-96 overflow-y-auto mb-6">
            <div className="space-y-4">
              {relevantMetadata.map((image, displayIndex) => {
                const originalIndex = images.findIndex(img => img.id === image.id);
                return (
                  <div key={image.id} className="py-3 border-b border-gray-100">
                    <div className="mb-3">
                      <div className="text-xs text-gray-500 space-y-1">
                        <div><strong>Source URL:</strong> {image.metadata.sourceUrl|| 'N/A'}</div>
                        <div><strong>Caption:</strong> {image.metadata.caption || 'N/A'}</div>
                        <div><strong>Title:</strong> {image.metadata.title || 'N/A'}</div>
                        <div><strong>Source Title:</strong> {image.metadata.sourceTitle || 'N/A'}</div>
                        <div><strong>Source Summary:</strong> {image.metadata.sourceSummary || 'N/A'}</div>
                        <div><strong>Source Content:</strong> {image.metadata.sourceContent || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="flex space-x-6">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`metadata_utility_${originalIndex}`}
                          value="somewhat_useful"
                          checked={data[`metadata_utility_${originalIndex}`] === 'somewhat_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`metadata_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('somewhat_useful')}`}>Somewhat useful</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name={`metadata_utility_${originalIndex}`}
                          value="highly_useful"
                          checked={data[`metadata_utility_${originalIndex}`] === 'highly_useful'}
                          onChange={(e) => {
                            const value = e.target.value;
                            setData(prev => ({ ...prev, [`metadata_utility_${originalIndex}`]: value }));
                          }}
                          className="w-4 h-4 text-sky-600 focus:ring-sky-600 mr-2"
                        />
                        <span className={`text-sm ${getStatusColor('highly_useful')}`}>Highly useful</span>
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      );
    } else {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">Step {step + 1} content coming soon...</p>
        </div>
      );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto" ref={scrollContainerRef}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">{getStepTitle()}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>
        
        {/* Progress Bar */}
        <div className="mb-6">
          <div className="flex justify-between text-xs text-gray-500 mb-2">
            <span>Step {step + 1} of {totalSteps}</span>
            <span>{Math.round(((step + 1) / totalSteps) * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-gradient-to-r from-sky-500 to-sky-700 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
            />
          </div>
        </div>
        
        {renderStepContent()}

        <div className="flex justify-end mt-6">
          {((step === 0 && isStep1Complete) || 
            (step === 1 && isStep2Complete) || 
            (step === 2 && isStep3Complete) || 
            (step === 3 && isStep4Complete) || 
            (step === 4 && isStep5Complete)) ? (
            (step < totalSteps - 1 && !(step === 3 && isStep4Complete && images.every((_, index) => data[`metadata_${index}`] === 'not_relevant'))) ? (
              <button
                onClick={handleNext}
                className="px-4 py-2 bg-sky-600 text-white rounded-md hover:bg-sky-700"
              >
                Next
              </button>
            ) : (
              <button
                onClick={async () => {
                  setIsSubmitting(true);
                  const finalData = { ...data };
                  const allImagesNotRelevant = images.every((_, index) => data[`image_${index}`] === 'not_relevant');
                  if (allImagesNotRelevant) {
                    images.forEach((_, index) => {
                      if (!finalData[`image_utility_${index}`]) {
                        finalData[`image_utility_${index}`] = "skipped because all images were marked irrelevant";
                      }
                    });
                  }
                  const allMetadataNotRelevant = images.every((_, index) => data[`metadata_${index}`] === 'not_relevant');
                  if (allMetadataNotRelevant) {
                    images.forEach((_, index) => {
                      if (!finalData[`metadata_utility_${index}`]) {
                        finalData[`metadata_utility_${index}`] = "skipped because all metadata were marked irrelevant";
                      }
                    });
                  }
                  
                  const dataWithQuery = {
                    ...finalData,
                    textQuery: searchTerm || '',
                    imageUrl: uploadedImageUrl || '',
                    answer_0_text: answers[0] || '',
                    answer_0_type: 'text_grounding_answer',
                    answer_1_text: answers[1] || '',
                    answer_1_type: 'image_grounding_answer', 
                    answer_2_text: answers[2] || '',
                    answer_2_type: 'parametric_answer'
                  };
                  console.log('Annotation Data:', dataWithQuery);
                  try {
                    const s3Key = await annotationUploader.uploadAnnotationToS3(dataWithQuery);
                    console.log('Annotation uploaded to S3:', s3Key);
                    onClose(true); 
                  } catch (error) {
                    console.error('Failed to upload annotation:', error);
                    onClose(false); 
                  } finally {
                    setIsSubmitting(false);
                  }
                }}
                className="px-4 py-2 bg-green-700 text-white rounded-md hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-t-2 border-t-white border-gray-300 rounded-full animate-spin mr-2"></div>
                    Aggregating your annotations...
                  </>
                ) : (
                  'Done'
                )}
              </button>
            )
          ) : (
            <button
              className="px-4 py-2 bg-gray-400 text-white rounded-md cursor-not-allowed"
              disabled
            >
              Complete all answers
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Status Message Component
const StatusMessage = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-700' : 'bg-red-700';
  
  return (
    <div className={`fixed top-4 left-1/2 transform -translate-x-1/2 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transition-all duration-300`}>
      {message}
    </div>
  );
};

// Main App component
const App = () => {
  let currentStage = getStage() || 'DEV';
  const [user, setUser] = useState(null);
  const [stage, setStage] = useState(currentStage);
  const [region, setRegion] = useState(getRegion(currentStage) || 'us-west-2');
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loadingText, setLoadingText] = useState('');
  const [uploadedImageUrl, setUploadedImageUrl] = useState('');
  const [imageSelected, setImageSelected] = useState(false);
  const [textSelected, setTextSelected] = useState(false);

  const [statusMessage, setStatusMessage] = useState(null);
  

  function createAmplifyListener() {
      Hub.listen('auth', ({ payload: { event, data } }) => {
        switch (event) {
          case 'signIn':
          case 'cognitoHostedUI':
            console.debug("In cognito hosted ui: ", data)
            setUser({ user: data });
            break;
          case 'signIn_failure':
          case 'cognitoHostedUI_failure':
            console.debug('Sign in failure', data);
            break;
          default:
            console.debug("Event didn't match any of the expected events: ", event);
        }
      });
    }

    async function doCognitoAuth() {
      //fetch the user credentials if midway has already authenticated the user.
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUser({ user: user });
        console.debug('Authenticated user ', user.getUsername());
      } catch (error) {
        console.debug('Not signed in. Error: ', error);
        //if couldn't fetch credentials, sign in the user by federate:
        await Auth.federatedSignIn({ customProvider: "FederateOIDC" });
      }
    }

    useEffect(() => {
      initializeAmplify(stage, region);
      createAmplifyListener();
      const doAuth = async () => {
        await doCognitoAuth();
      }
      doAuth();
    }, [stage, region]);

  // Effect to update isUploaded based on uploadedImageUrl
  const [isUploaded, setIsUploaded] = useState(false);
  useEffect(() => {
    setIsUploaded((uploadedImageUrl || '').trim() !== '');
  }, [uploadedImageUrl]);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [imageUrlInput, setImageUrlInput] = useState('');
  const [displayedImageUrl, setDisplayedImageUrl] = useState('');

  const [showEnlargeModal, setShowEnlargeModal] = useState(false);
  const [enlargedImageUrl, setEnlargedImageUrl] = useState('');

  const [visualQaHandler, setVisualQaHandler] = useState(null);
  const [annotationUploader, setAnnotationUploader] = useState(null);
  const [imageUploader, setImageUploader] = useState(null);
  const [qaResponses, setQaResponses] = useState({});
  const [showAnnotateModal, setShowAnnotateModal] = useState(false);
  const [annotateData, setAnnotateData] = useState({});
  const [annotateStep, setAnnotateStep] = useState(0);

  useEffect(() => {
    if (visualQaExperimentalApiHost && visualQaExperimentalApiHost[stage] && visualQaExperimentalApiHost[stage][region]) {
      setVisualQaHandler(new VisualQaHandler(visualQaExperimentalApiHost[stage][region]));
    }
    if (visualQaDataUploadApiHost && visualQaDataUploadApiHost[stage] && visualQaDataUploadApiHost[stage][region]) {
      setAnnotationUploader(new AnnotationUploader(visualQaDataUploadApiHost[stage][region]));
      setImageUploader(new ImageUploader(visualQaDataUploadApiHost[stage][region]));
    }
  }, [stage, region]);

  const handleSearchSubmit = async (fromInitialScreen = false) => {
    if ((searchTerm || '').trim() === '' && (uploadedImageUrl || '').trim() === '') {
      setErrorMessage('Please enter a query or upload a file.');
      return;
    }
    setErrorMessage('');
    // Reset annotation exercise for new search
    setAnnotateData({});
    setAnnotateStep(0);
    setIsLoading(true);

    const loadingMessages = [
      "Loading..."
    ];
    let messageIndex = 0;
    setLoadingText(loadingMessages[messageIndex]);

    const intervalId = setInterval(() => {
      messageIndex = (messageIndex + 1) % loadingMessages.length;
      setLoadingText(loadingMessages[messageIndex]);
    }, 3000);

    try {
      let response = await visualQaHandler.getVisualQaExperimentalResponses(searchTerm, uploadedImageUrl, imageSelected, textSelected, true);
      
      // Clear the interval and update state immediately after receiving response
      clearInterval(intervalId);
      setIsLoading(false);
      setQaResponses(response);
      
      if (fromInitialScreen) {
        setCurrentStep(1);
        setShowResults(true);
      } else {
        setShowResults(true);
      }
    } catch (error) {
      clearInterval(intervalId);
      setIsLoading(false);
      setErrorMessage('Error fetching responses: ' + (error?.message || 'Unknown error'));
    }
  };

  const openUploadModal = () => {
    setShowUploadModal(true);
    setImageUrlInput('');
    setDisplayedImageUrl('');
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
  };

  const openEnlargeModal = (url) => {
    setEnlargedImageUrl(url);
    setShowEnlargeModal(true);
  };

  const closeEnlargeModal = () => {
    setShowEnlargeModal(false);
    setEnlargedImageUrl('');
  };

  const openAnnotateModal = () => {
    console.log('Opening annotate modal');
    setShowAnnotateModal(true);
  };

  const closeAnnotateModal = (success = null) => {
    console.log('Closing annotate modal');
    setShowAnnotateModal(false);
    setAnnotateData({});
    setAnnotateStep(0);
    
    // Show status message based on success/failure
    if (success === true) {
      setStatusMessage({
        message: 'Your annotations were successfully received. Thank you for your time!',
        type: 'success'
      });
    } else if (success === false) {
      setStatusMessage({
        message: 'Something went wrong with your annotations. Try again',
        type: 'error'
      });
    } else if (success === 'annotation_failed') {
      setStatusMessage({
        message: 'Sorry, we failed to create an annotation exercise for you at this point. Refresh and try again.',
        type: 'error'
      });
    }
  };

  return (
    <div className="font-sans antialiased text-graphite-gray bg-bone-white min-h-screen flex flex-col">
      {/* Status Message */}
      {statusMessage && (
        <StatusMessage 
          message={statusMessage.message}
          type={statusMessage.type}
          onClose={() => setStatusMessage(null)}
        />
      )}      
      {/* Annotate Button - Only show on results page and not during loading */}
      {currentStep === 1 && !isLoading && (
        <div className="fixed top-4 right-4 z-40">
          <button
          onClick={openAnnotateModal}
          className="bg-gray-100 text-sky-700 text-sm hover:bg-gray-200 hover:text-sky-800 p-2 rounded transition-all duration-200 flex items-center justify-center"
          title="Annotate" >
            <SquarePen className="mr-1" size={12} /> <span>Annotate</span>
          </button>
        </div>
      )}
      <style>
        {`
          body {
            font-family: 'Space Grotesk', sans-serif;
            line-height: 1.6;
          }
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          .backdrop-blur-md {
            backdrop-filter: blur(10px);
            -webkit-backdrop-filter: blur(10px);
          }
        `}
      </style>
      {isLoading ? (
        <LoadingSpinner loadingText={loadingText} />
      ) : currentStep === 0 ? (
        <InitialSearchScreen
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          errorMessage={errorMessage}
          handleSearchSubmit={handleSearchSubmit}
          openUploadModal={openUploadModal}
          uploadedImageUrl={uploadedImageUrl}
          imageSelected={imageSelected}
          setImageSelected={setImageSelected}
          textSelected={textSelected}
          setTextSelected={setTextSelected}
        />
      ) : (
        <MainInteractionScreen
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          errorMessage={errorMessage}
          handleSearchSubmit={handleSearchSubmit}
          openUploadModal={openUploadModal}
          uploadedImageUrl={uploadedImageUrl}
          setShowResults={setShowResults}
          qaResponses={qaResponses}
          imageSelected={imageSelected}
          setImageSelected={setImageSelected}
          textSelected={textSelected}
          setTextSelected={setTextSelected}
        />
      )}
      <UploadModal
        show={showUploadModal}
        onClose={closeUploadModal}
        imageUrlInput={imageUrlInput}
        setImageUrlInput={setImageUrlInput}
        displayedImageUrl={displayedImageUrl}
        setDisplayedImageUrl={setDisplayedImageUrl}
        openEnlargeModal={openEnlargeModal}
        imageUploader={imageUploader}
        setUploadedImageUrl={setUploadedImageUrl}
      />
      <EnlargeImageModal
        show={showEnlargeModal}
        onClose={closeEnlargeModal}
        imageUrl={enlargedImageUrl}
      />
      <AnnotateModal
        show={showAnnotateModal}
        onClose={closeAnnotateModal}
        data={annotateData}
        setData={setAnnotateData}
        step={annotateStep}
        setStep={setAnnotateStep}
        searchTerm={searchTerm}
        uploadedImageUrl={uploadedImageUrl}
        annotationUploader={annotationUploader}
        qaResponses={qaResponses}
        visualQaHandler={visualQaHandler}
        imageSelected={imageSelected}
        textSelected={textSelected}
      />
    </div>
  );
};

export default App;
